import { EVENTS, publish } from 'ia-util-pubsub';

class IaSubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  get sectionId() {
    return this.getAttribute('section-id');
  }
  get productId() {
    return this.getAttribute('product-id');
  }
  get cadence() {
    return this.getAttribute('cadence');
  }
  set cadence(cadence) {
    this.setAttribute('cadence', cadence);
  }

  setElements() {
    this.planSelect = this.querySelector('[data-selling-plan-select]');
    this.cadenceSelects = this.querySelectorAll('[data-cadence-select]');
    this.cadenceBlocks = this.querySelectorAll('[data-cadence]');
  }

  connectedCallback() {
    // Initialize when element is connected to DOM
    if (!this.initialized) {
      this.init();
    }
  }

  init() {
    try {
      this.section = this.closest('.shopify-section');
      this.form = this.section?.querySelector('ia-product-buy form[action*="/cart/add"]');
      
      const widgetData = this.querySelector('[data-widget-data]');
      if (widgetData) {
        this.data = JSON.parse(widgetData.textContent);
        this.sellingPlanGroups = this.data?.selling_plan_groups;
        this.variantPrice = this.data?.variant_price;
        this.planPrice = this.data?.plan_price;
      }

    this.setElements();
    this.setupEventListeners();
    this.updateCadenceActiveState();
    this.toggleInfo();
    this.initialized = true;

    // The server renders the one-time price into the buy button, but the
    // widget may start with Subscribe & Save selected — sync the button once
    // every module has subscribed (window load; no rAF — it stalls in
    // background tabs)
    if (this.cadence === 'subscription') {
      const sync = () => this.publishEvent(EVENTS.cadenceChange);
      if (document.readyState === 'complete') {
        sync();
      } else {
        window.addEventListener('load', sync, { once: true });
      }
    }
    } catch (error) {
      console.error('Error initializing subscription widget', error);
    }
  }

  setupEventListeners() {
    // Store bound methods to avoid creating new ones each time
    this.boundPlanChange = this.planChange.bind(this);
    this.boundCadenceChange = this.cadenceChange.bind(this);
    this.boundCadenceBlockClick = this.cadenceBlockClick.bind(this);
    this.boundRefresh = this.refresh.bind(this);

    if (this.planSelect) {
      this.planSelect.addEventListener('change', this.boundPlanChange);
    }
    
    if (this.cadenceSelects) {
      this.cadenceSelects.forEach((cadenceSelect) => {
        cadenceSelect.addEventListener('change', this.boundCadenceChange);
      });
    }
    
    // Make entire cadence block clickable
    if (this.cadenceBlocks) {
      this.cadenceBlocks.forEach((block) => {
        block.addEventListener('click', this.boundCadenceBlockClick);
      });
    }
    
    if (this.form) {
      this.form.addEventListener('product:rerender', this.boundRefresh);
    }
  }

  removeEventListeners() {
    if (this.planSelect && this.boundPlanChange) {
      this.planSelect.removeEventListener('change', this.boundPlanChange);
    }
    if (this.cadenceSelects && this.boundCadenceChange) {
      this.cadenceSelects.forEach((cadenceSelect) => {
        cadenceSelect.removeEventListener('change', this.boundCadenceChange);
      });
    }
    if (this.cadenceBlocks && this.boundCadenceBlockClick) {
      this.cadenceBlocks.forEach((block) => {
        block.removeEventListener('click', this.boundCadenceBlockClick);
      });
    }
    if (this.form && this.boundRefresh) {
      this.form.removeEventListener('product:rerender', this.boundRefresh);
    }
  }

  refresh(e) {
    if (e?.detail?.htmlFragment && e.detail.htmlFragment.querySelector('ia-product-subscription')) {
      this.removeEventListeners();
      this.innerHTML = e.detail.htmlFragment.querySelector('ia-product-subscription').innerHTML;
    }

    const widgetData = this.querySelector('[data-widget-data]');
    if (widgetData) {
      this.data = JSON.parse(widgetData.textContent);
      this.sellingPlanGroups = this.data?.selling_plan_groups;
      this.variantPrice = this.data?.variant_price;
      this.planPrice = this.data?.plan_price;
    }

    this.setElements();
    this.setupEventListeners();
    this.updateCadenceActiveState();
    this.toggleInfo();
  }

  findPlanById(planId) {
    let foundPlan = null;

    // Search through selling plan groups to find the plan
    if (this.sellingPlanGroups) {
      this.sellingPlanGroups.forEach(group => {
        group.selling_plans.forEach(plan => {
          if (plan.id.toString() === planId) {
            foundPlan = plan;
          }
        });
      });
    }
    
    return foundPlan;
  }

  publishEvent(eventName) {
    const planId = this.planSelect?.value;
    const planOption = this.planSelect?.options[this.planSelect.selectedIndex];
    const planPrice = planOption?.dataset?.planPrice;
    const planPriceCents = parseInt(planOption?.dataset?.planPriceCents, 10);
    const plan = planId ? this.findPlanById(planId) : null;

    const detail = {
      detail: {
        sectionId: this.sectionId,
        productId: this.productId,
        plan: plan,
        cadence: this.cadence,
        variant_price: this.variantPrice,
        variant_price_cents: this.data?.variant_price_cents,
        plan_price: planPrice,
        plan_price_cents: isNaN(planPriceCents) ? this.data?.plan_price_cents : planPriceCents,
        currency: this.data?.currency,
        current_price: this.cadence === 'subscription' ? this.planPrice : this.variantPrice
      }
    }

    publish(eventName, detail);
  }

  updateCadenceActiveState() {
    if (!this.cadenceBlocks) return;
    
    this.cadenceBlocks.forEach((block) => {
      const cadenceValue = block.dataset.cadence;
      const radio = block.querySelector('[data-cadence-select]');
      
      if (cadenceValue === this.cadence) {
        block.classList.add('is-active');
        if (radio) {
          radio.checked = true;
        }
      } else {
        block.classList.remove('is-active');
        if (radio) {
          radio.checked = false;
        }
      }
    });
  }

  cadenceBlockClick(event) {
    // Don't trigger if clicking on the select dropdown
    if (event.target.closest('.ia-subscription-select')) {
      return;
    }
    
    const block = event.currentTarget;
    const newCadence = block.dataset.cadence;
    
    if (newCadence) {
      this.cadence = newCadence;
      this.updateCadenceActiveState();
      this.toggleInfo();
      this.publishEvent(EVENTS.cadenceChange);
    }
  }

  cadenceChange(event) {
    if (event.target.type === 'checkbox') {
      this.cadence = event.target.checked ? 'subscription' : 'one-time';
    } else {
      this.cadence = event.target.value;
    }
    this.updateCadenceActiveState();
    this.toggleInfo();
    this.publishEvent(EVENTS.cadenceChange);
  }

  toggleInfo() {
    const subscriptionInfo = this.querySelectorAll('[data-subscription-info]');
    const onetimeInfo = this.querySelectorAll('[data-onetime-info]');
    if (this.cadence === 'subscription') {
      subscriptionInfo.forEach(info => info.classList.remove('ia-visually-hidden'));
      onetimeInfo.forEach(info => info.classList.add('ia-visually-hidden'));
    } else {
      subscriptionInfo.forEach(info => info.classList.add('ia-visually-hidden'));
      onetimeInfo.forEach(info => info.classList.remove('ia-visually-hidden'));
    }
  }

  planChange() {
    this.publishEvent(EVENTS.planChange);
  }
}

if (!customElements.get('ia-product-subscription')) {
  customElements.define('ia-product-subscription', IaSubscriptionWidget);
}
